/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint-disable-next-line @nx/enforce-module-boundaries
import { usePageContext } from '@marriott/mi-shop-components';
import { usePageContextZustandStore } from '../../modules/store/pageContextZustandStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { usePageContextReturnType } from '../../context/PageContext/index.types';

interface UseConditionalPageContextOptions {
  /**
   * Specify which component is using this hook to determine the correct feature flag
   * 'adf' will check EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND (for AvailabilityCalendar and CalendarWrapper)
   * 'serp' will check EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND (for all other components)
   */
  flagType?: 'adf' | 'serp';
}

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 */
export const useConditionalPageContext = (
  options: UseConditionalPageContextOptions = { flagType: 'serp' }
): usePageContextReturnType & any => {
  const { flagType = 'serp' } = options;

  // Get feature flags from the client environment store
  const featureFlags = (useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) || {}) as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Get data from React Context (fallback)
  const pageContext = usePageContext();

  // Get the entire Zustand store (including all dynamically added properties)
  const zustandStore = usePageContextZustandStore();

  // Initialize Zustand store with context data if using Zustand and store is empty
  // This ensures seamless migration from context to store
  if (shouldUseZustand && pageContext && Object.keys(zustandStore.pageState || {}).length === 0) {
    zustandStore.initializePageContextStore(pageContext);
  }

  if (shouldUseZustand) {
    // Return the entire Zustand store (including all properties added via Object.assign)
    // This ensures all properties from memoPageContexValue are available
    return zustandStore as any;
  } else {
    // Return React Context data with compatibility wrapper for updateComponentState and updatePageErrors
    return {
      ...pageContext,
      // Provide default implementations if not available in context
      updateComponentState: pageContext?.updateComponentState || (() => {}),
      updatePageErrors: pageContext?.updatePageErrors || (() => {}),
    };
  }
};

/**
 * Convenience hook specifically for ADF components (AvailabilityCalendar, CalendarWrapper)
 * that checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag
 */
export const useConditionalPageContextADF = () => {
  return useConditionalPageContext({ flagType: 'adf' });
};

/**
 * Convenience hook specifically for SERP components
 * that checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag
 */
export const useConditionalPageContextSERP = () => {
  return useConditionalPageContext({ flagType: 'serp' });
};
